import * as Common from '@piccolohealth/pbs-common';
import { LabelType } from '@piccolohealth/pbs-common';
import { ChakraV3 } from '@piccolohealth/ui';
import React from 'react';
import { FormSection } from '../../components/forms/FormSection';
import { FormStack } from '../../components/forms/FormStack';
import { HookedAvatarUploader } from '../../components/forms/HookedAvatarUploader';
import { HookedFormField } from '../../components/forms/HookedFormField';
import { HookedInput } from '../../components/forms/HookedInput';
import { HookedLabelChooser } from '../../components/forms/HookedLabelChooser';
import { HookedLocationChooser } from '../../components/forms/HookedLocationChooser';
import { HookedSelect } from '../../components/forms/HookedSelect';
import { HookedSingleDatepicker } from '../../components/forms/HookedSingleDatepicker';
import { FormErrorsAlert } from '../../components/generic/FormErrorsAlert';
import { participantStatusOptions } from './ParticipantStatusTag';

interface Props {
  isDisabled?: boolean;
}

export const ParticipantFormGeneral = (props: Props) => {
  const { isDisabled } = props;

  return (
    <FormStack>
      <FormErrorsAlert />
      <FormSection heading='Basic details' description='Who is the participant?'>
        <FormStack orientation='horizontal'>
          <HookedFormField name='firstName' label='First name' required disabled={isDisabled}>
            <HookedInput name='firstName' size='sm' placeholder='Enter first name' />
          </HookedFormField>
          <HookedFormField name='lastName' label='Last name' required disabled={isDisabled}>
            <HookedInput name='lastName' size='sm' placeholder='Enter last name' />
          </HookedFormField>
        </FormStack>

        <FormStack orientation='horizontal'>
          <HookedFormField name='dob' label='Date of birth' required disabled={isDisabled}>
            <HookedSingleDatepicker
              name='dob'
              size='sm'
              placeholder='Pick a date'
              isDisabled={isDisabled}
            />
          </HookedFormField>
          <HookedFormField name='gender' label='Gender' required disabled={isDisabled}>
            <HookedSelect
              name='gender'
              size='sm'
              placeholder='Choose a gender'
              isDisabled={isDisabled}
              options={[
                {
                  label: Common.renderGender(Common.Gender.Male),
                  value: Common.Gender.Male,
                  raw: Common.Gender.Male,
                },
                {
                  label: Common.renderGender(Common.Gender.Female),
                  value: Common.Gender.Female,
                  raw: Common.Gender.Female,
                },
                {
                  label: Common.renderGender(Common.Gender.Transgender),
                  value: Common.Gender.Transgender,
                  raw: Common.Gender.Transgender,
                },
                {
                  label: Common.renderGender(Common.Gender.GenderNeutral),
                  value: Common.Gender.GenderNeutral,
                  raw: Common.Gender.GenderNeutral,
                },
                {
                  label: Common.renderGender(Common.Gender.NonBinary),
                  value: Common.Gender.NonBinary,
                  raw: Common.Gender.NonBinary,
                },
                {
                  label: Common.renderGender(Common.Gender.NotSpecified),
                  value: Common.Gender.NotSpecified,
                  raw: Common.Gender.NotSpecified,
                },
              ]}
            />
          </HookedFormField>
        </FormStack>

        <FormStack orientation='horizontal'>
          <HookedFormField name='status' label='Status' required>
            <HookedSelect
              name='status'
              size='sm'
              optionVariant='tag'
              placeholder='Choose a status'
              options={Object.values(participantStatusOptions)}
            />
          </HookedFormField>

          <HookedFormField name='locationId' label='Location' required disabled={isDisabled}>
            <HookedLocationChooser name='locationId' size='sm' isDisabled={isDisabled} />
          </HookedFormField>
        </FormStack>

        <FormStack orientation='horizontal'>
          <HookedFormField name='ndisNumber' label='NDIS number' disabled={isDisabled}>
            <HookedInput name='ndisNumber' size='sm' placeholder='Enter NDIS number' />
          </HookedFormField>
          <HookedFormField name='email' label='Email' disabled={isDisabled}>
            <HookedInput name='email' size='sm' placeholder='Enter email address' />
          </HookedFormField>
        </FormStack>

        <FormStack orientation='horizontal'>
          <HookedFormField name='phone' label='Phone number' disabled={isDisabled}>
            <HookedInput name='phone' size='sm' placeholder='Enter phone number' />
          </HookedFormField>
          <HookedFormField name='labelIds' label='Labels' disabled={isDisabled}>
            <HookedLabelChooser
              name='labelIds'
              type={LabelType.Participant}
              isDisabled={isDisabled}
            />
          </HookedFormField>
        </FormStack>

        <HookedFormField name='picture' label='Profile picture' disabled={isDisabled}>
          <HookedAvatarUploader
            name='picture'
            w='96px'
            h='96px'
            direction='row'
            isDisabled={isDisabled}
          />
        </HookedFormField>
      </FormSection>

      <ChakraV3.Separator />

      <FormSection heading='Address details' description='Where is the participant located?'>
        <FormStack orientation='horizontal'>
          <HookedFormField name='address' label='Address' disabled={isDisabled}>
            <HookedInput name='address' size='sm' placeholder='Enter address' />
          </HookedFormField>
          <HookedFormField name='suburb' label='Suburb' disabled={isDisabled}>
            <HookedInput name='suburb' size='sm' placeholder='Enter suburb' />
          </HookedFormField>
        </FormStack>

        <FormStack orientation='horizontal'>
          <HookedFormField name='state' label='State' disabled={isDisabled}>
            <HookedSelect
              name='state'
              size='sm'
              placeholder='Choose a state'
              isDisabled={isDisabled}
              options={Common.STATES.map((state) => ({
                label: state.long,
                value: state.short,
                raw: state,
              }))}
            />
          </HookedFormField>
          <HookedFormField name='postcode' label='Postcode' disabled={isDisabled}>
            <HookedInput name='postcode' size='sm' placeholder='Enter postcode' />
          </HookedFormField>
        </FormStack>
      </FormSection>
    </FormStack>
  );
};
